import { useState } from "react";
import {
  Button,
  Box,
  TextField,
  Paper,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  addButtonPadding,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import Grid from "@mui/material/Grid";
import "../../common/Common.css";
import { DataGrid } from "@mui/x-data-grid";
import ServiceCreationForm from "./ServiceCreationForm";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import BorderColorRoundedIcon from "@mui/icons-material/BorderColorRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import validators from "../../utils/validations/validators";
import api from "../../services/api";

// ---- shared palette (matches PatientTable.js) ---------------------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};

const actionIconBtn = (color, hoverBg) => ({
  width: 30,
  height: 30,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color,
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: hoverBg },
});

export default function ServiceTable(props) {
  const { branchId, getServices, rows } = props;
  const [open, setOpen] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [serviceID, setServiceID] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({});
  const handleOpen = () => setOpen(true);
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    if (reason === "escapeKeyDown") {
      return;
    }
    setFormData({});
    setValidationErrors({});
    setOpen(false);
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 600, color: palette.tealDark }}>
          {params.value}
        </span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "consultation_fee",
      headerName: "Consultation Fee",
      type: "number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "therapy_session_fee",
      headerName: "Therapy Session Fee",
      type: "number",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "therapy_duration_minutes",
      headerName: "Therapy Duration Minutes",
      type: "number",
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            gap: "6px",
          }}
        >
          <Tooltip title="Edit service">
            <Box
              sx={actionIconBtn(palette.slate, "#F1F5F9")}
              onClick={() => {
                handleServiceEdit(params.row.id);
              }}
            >
              <BorderColorRoundedIcon sx={{ fontSize: "17px" }} />
            </Box>
          </Tooltip>
          <Tooltip title="Delete service">
            <Box
              sx={actionIconBtn(palette.coral, palette.coralSoft)}
              onClick={() => {
                handleServiceDelete(params.row.id);
              }}
            >
              <DeleteRoundedIcon sx={{ fontSize: "17px" }} />
            </Box>
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Validate live
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: errorMsg,
      }));
    }
  };

  const requiredFields = [
    "name",
    "description",
    "consultation_fee",
    "therapy_session_fee",
    "therapy_duration_minutes",
  ];

  const isFormValid =
    Object.values(validationErrors).every((err) => err === "") &&
    requiredFields.every(
      (field) =>
        formData[field] !== "" &&
        formData[field] !== null &&
        formData[field] !== undefined
    );

  const handleCreateService = async () => {
    if (!isFormValid) {
      setSnackbarOpen({
        status: true,
        message: "Please fill all required fields correctly.",
        severity: "error",
      });
      return;
    }
    if (isEditClicked) {
      sendServiceEdit();
    } else {
      const updatedFormData = {
        ...formData,
        branch_id: branchId,
      };
      try {
        const response = await api.post(
          `app/management/therapy-service/`,
          updatedFormData,
        );
        if (response.status === 200) {
          setFormData({});
          setSnackbarOpen({
            status: true,
            message: "Service added successfully!",
          });
          handleClose();
          getServices();
        } else {
          setSnackbarOpen({
            status: true,
            message: "Something went wrong. Please try again later.",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    }
  };

  const handleServiceEdit = async (id) => {
    setServiceID(id);
    setIsEditClicked(true);
    await getDetailsServiceWise(id);
    handleOpen();
  };

  const getDetailsServiceWise = async (id) => {
    try {
      let serviceData = await api.get(
        `app/management/therapy-service/${id}`,
      );
      if (serviceData?.status === 200) {
        setFormData(serviceData?.data);
      } else {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const sendServiceEdit = async () => {
    try {
      let updatedServiceData = await api.put(
        `app/management/therapy-service/${serviceID}`,
        formData,
      );
      if (updatedServiceData?.status === 200) {
        setFormData({});
        setIsEditClicked(false);
        setSnackbarOpen({
          status: true,
          message: "Service Updated successfully!",
        });
        handleClose();
        getServices();
      } else {
        setSnackbarOpen({
          status: true,
          message: "Something went wrong. Please try again later.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const handleServiceDelete = async (id) => {
    try {
      let deletedServiceData = await api.delete(
        `app/management/therapy-service/${id}`,
      );
      if (deletedServiceData?.status === 200) {
        setSnackbarOpen({
          status: true,
          message: "Service Deleted successfully!",
        });
        getServices();
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: "Something went wrong. Please try again later.",
        severity: "error",
      });
    }
  };

  const handleSnackClose = () => setSnackbarOpen(false);

  return (
    <>
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
      <Box className="tabcontent_box" sx={{ backgroundColor: palette.bg }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            padding: "16px 20px",
            marginBottom: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Grid
            container
            spacing={2}
            className="patientTable-btnstack"
            display="flex"
            alignItems="center"
          >
            <Grid size={8} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button
                variant="contained"
                startIcon={<MedicalServicesRoundedIcon sx={{ fontSize: "18px" }} />}
                sx={[
                  registerPatientButtonStyles,
                  addButtonPadding,
                  {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    backgroundColor: palette.teal,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: palette.tealDark,
                      boxShadow: "none",
                    },
                  },
                ]}
                size="small"
                onClick={handleOpen}
              >
                Add New Service
              </Button>
              <span style={{ fontSize: "13px", color: palette.slate }}>
                {filteredRows?.length ?? 0} service
                {filteredRows?.length === 1 ? "" : "s"}
              </span>
            </Grid>
            <Grid size={4} sx={{ textAlign: "right" }}>
              <TextField
                size="small"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: palette.bg,
                    fontSize: "13.5px",
                    "& fieldset": { borderColor: palette.line },
                    "&:hover fieldset": { borderColor: palette.teal },
                    "&.Mui-focused fieldset": { borderColor: palette.teal },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: "18px", color: palette.slate }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            overflow: "hidden",
          }}
        >
          <DataGrid
            sx={{
              fontFamily: "Nunito, sans-serif",
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: palette.bg,
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "12.5px",
                fontWeight: 700,
                color: palette.slate,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13px",
                borderBottom: `1px solid ${palette.line}`,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: palette.tealSoft,
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${palette.line}`,
              },
            }}
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
            isCellEditable={() => false}
          />
        </Paper>
      </Box>
      <ServiceCreationForm
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        formData={formData}
        setFormData={setFormData}
        handleInputChange={handleInputChange}
        handleCreateService={handleCreateService}
        isEditClicked={isEditClicked}
        isFormValid={isFormValid}
        validationErrors={validationErrors}
      />
    </>
  );
}